import { useState } from 'react';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase';
import { GoogleIcon, AppleIcon } from './authIcons';

const providerFor = (mode) => {
  if (mode === 'apple') {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return provider;
  }
  return new GoogleAuthProvider();
};

// Opened as a standalone top-level tab by oauthPopup.js, to escape a
// third-party iframe (e.g. this app embedded in Shopify admin) where Google's
// popup/FedCM sign-in can't complete.
//
// Two things were tried and ruled out here:
// - signInWithPopup fired automatically on mount: failed with
//   auth/popup-blocked, because nothing triggered it was a direct user
//   gesture (it ran from a useEffect/promise chain, not a click) — browsers
//   require the popup-opening call itself to be in the synchronous call
//   stack of a click.
// - signInWithRedirect fired automatically on mount: got stuck in an
//   infinite reload loop — this window's pending-redirect state (how
//   Firebase remembers "a redirect is in flight" across the navigation to
//   Google and back) didn't survive the round trip reliably in a
//   script-opened window, so every return trip looked like a fresh load and
//   re-triggered the redirect.
//
// Gating signInWithPopup behind a real click here satisfies the user-gesture
// requirement while staying a real top-level window, so there's no nested
// framing or storage-partitioning problem either.
const AuthPopup = () => {
  const [error, setError] = useState(null);
  const mode = new URLSearchParams(window.location.search).get('mode') || 'google';

  const signIn = () => {
    setError(null);
    signInWithPopup(auth, providerFor(mode))
      .then(async (result) => {
        const idToken = await result.user.getIdToken();
        window.opener?.postMessage(
          { source: 'shopik-oauth', action: 'oauthComplete', idToken },
          window.location.origin
        );
        window.close();
      })
      .catch((err) => {
        console.error(err);
        setError(err.message);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 16, textAlign: 'center', padding: 24 }}>
      <p>Continue signing in with {mode === 'apple' ? 'Apple' : 'Google'}.</p>
      <button className="auth-oauth-button" onClick={signIn}>
        {mode === 'apple' ? <AppleIcon /> : <GoogleIcon />} Continue with {mode === 'apple' ? 'Apple' : 'Google'}
      </button>
      {error && <div className="auth-error">{error}</div>}
    </div>
  );
};

export default AuthPopup;
